"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function NavigationLinks({
  mobile = false,
  onItemClick
}: {
  mobile?: boolean;
  onItemClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className={mobile ? "flex flex-col gap-4" : "hidden md:flex items-center gap-8"}>
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className={mobile ? "flex flex-col gap-4" : "hidden md:flex items-center gap-8"}>
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onItemClick} data-index={i} className={`
            relative font-medium text-secondary-foreground hover:text-foreground transition-colors
            ${mobile ? "block py-3 text-lg border-b border-border/50" : "py-2 text-sm"}
            after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-center 
            after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 
            hover:after:scale-x-100
          `}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);
  return <header id="header" className={`dark bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center gap-2 group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582830/img/image_yqzzm4ga.png" alt="Imperium Technologies" className="drop-shadow-[0_0_8px_rgba(255,255,255,0.1)] transition-transform duration-300 group-hover:scale-105 !p-0 !w-[70px] !h-[75px] ![margin:17px_0px_0px_0px]" />
              <div className="flex flex-col justify-center leading-none">
                <span className="font-bold tracking-wider text-foreground uppercase drop-shadow-[0_0_10px_rgba(255,255,255,0.1)] !text-[22px] !text-justify !h-[25px]">IMPERIUM</span>
                <span className="font-semibold tracking-[0.3em] text-primary uppercase mt-1 !text-[9px] !text-center">TECHNOLOGIES</span>
              </div>
            </Link>
          </div>

          {}
          <MenuProvider menu_id="46">
            <NavigationLinks />
          </MenuProvider>

          {}
          <div className="hidden md:flex items-center gap-4">
            <Button asChild variant="outline" className="border-border text-foreground hover:bg-accent hover:text-accent-foreground transition-colors">
              <Link to="/access">Client Portal</Link>
            </Button>
            
            <div className="relative group">
              {}
              <div className="absolute -inset-0.5 bg-primary rounded-md blur opacity-40 group-hover:opacity-60 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
              <Button asChild className="relative bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                <Link to="/access/?imp_tab=register">
                  Get Started
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="flex md:hidden items-center">
            <Button size="icon" variant="ghost" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-expanded={isMobileMenuOpen} aria-label="Toggle navigation menu" className="text-foreground hover:text-primary">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      <div className={`md:hidden fixed inset-x-0 top-20 bottom-0 bg-background/95 backdrop-blur-xl transition-all duration-300 ease-in-out overflow-y-auto border-t border-border ${isMobileMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full pointer-events-none"}`}>
        <div className="px-4 pt-6 pb-8 space-y-8">
          <MenuProvider menu_id="46">
            <NavigationLinks mobile onItemClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          
          <div className="flex flex-col gap-4 pt-4 border-t border-border/50">
            <Button asChild variant="outline" className="w-full justify-center border-border text-foreground h-12 text-base" onClick={() => setIsMobileMenuOpen(false)}>
              <Link to="/access">Client Portal</Link>
            </Button>
            <Button asChild className="w-full justify-center bg-primary text-primary-foreground h-12 text-base shadow-[0_0_15px_rgba(var(--primary),0.3)]">
              <Link to="/access" onClick={() => setIsMobileMenuOpen(false)}>
                Get Started
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </header>;
}